import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, ChevronRight, Save, CalendarDays, Users, UserCheck, UserX } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import ExportButton from './ExportButton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function snapToMonday(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function shiftWeek(dateStr, weeks) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

const WeeklyAttendance = ({ schools = [] }) => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'school_head', 'project_manager', 'staff']);

  const [weekStart, setWeekStart] = useState(() => snapToMonday(new Date().toISOString().slice(0, 10)));
  const [school, setSchool] = useState('all');
  const [studentClass, setStudentClass] = useState('all');
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState({}); // {studentId-date: status}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setDirty({});
    try {
      const params = new URLSearchParams({ week_start: weekStart });
      if (school !== 'all') params.append('school', school);
      if (studentClass !== 'all') params.append('student_class', studentClass);
      const res = await axios.get(`${API_URL}/api/personnel/students/attendance/week?${params}`);
      setGrid(res.data);
    } catch (err) {
      toast.error('Failed to load weekly attendance');
    } finally {
      setLoading(false);
    }
  }, [weekStart, school, studentClass]);

  useEffect(() => { load(); }, [load]);

  const setCell = (studentId, date, status) => {
    if (!canEdit) return;
    const key = `${studentId}__${date}`;
    setDirty((d) => ({ ...d, [key]: status }));
  };

  const getCellValue = (studentId, date) => {
    const key = `${studentId}__${date}`;
    if (key in dirty) return dirty[key];
    const row = grid?.rows?.find((r) => r.student_id === studentId);
    return row?.per_day?.[date] || '';
  };

  const handleSave = async () => {
    const entries = Object.entries(dirty)
      .map(([k, status]) => {
        const [student_id, date] = k.split('__');
        return { student_id, date, status };
      })
      .filter((e) => e.status === 'present' || e.status === 'absent' || e.status === 'late');
    if (entries.length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/personnel/students/attendance/week/bulk`,
        { entries },
      );
      toast.success(`Saved ${res.data.inserted + res.data.updated} record(s)`);
      setDirty({});
      load();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const classes = Array.from(new Set((grid?.rows || []).map((r) => r.student_class).filter(Boolean)));
  const dates = grid?.dates || [];

  const totals = grid?.totals || { students: 0, girls: 0, boys: 0 };
  const byDay = grid?.by_day || {};

  const exportParams = { week_start: weekStart };
  if (school !== 'all') exportParams.school = school;
  if (studentClass !== 'all') exportParams.student_class = studentClass;

  return (
    <div className="space-y-4" data-testid="weekly-attendance">
      {/* Controls */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setWeekStart(shiftWeek(weekStart, -1))}
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Week starting (Monday)</span>
              <Input
                type="date"
                value={weekStart}
                onChange={(e) => setWeekStart(snapToMonday(e.target.value))}
                className="w-44"
                data-testid="week-start-input"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setWeekStart(shiftWeek(weekStart, 1))}
              data-testid="next-week-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {schools.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">School</span>
              <Select value={school} onValueChange={setSchool}>
                <SelectTrigger className="w-44" data-testid="weekly-school-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All schools</SelectItem>
                  {schools.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {classes.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Class</span>
              <Select value={studentClass} onValueChange={setStudentClass}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1" />
          <ExportButton
            path="exports/weekly-attendance"
            filenameBase={`weekly_attendance_${weekStart}`}
            label="Export Week"
            extraParams={exportParams}
            testId="weekly-attendance-export-btn"
          />
          {canEdit && (
            <Button
              onClick={handleSave}
              disabled={saving || Object.keys(dirty).length === 0}
              data-testid="save-weekly-attendance-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : `Save${Object.keys(dirty).length > 0 ? ` (${Object.keys(dirty).length})` : ''}`}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><Users className="h-4 w-4" /> Students this week</CardDescription>
            <CardTitle className="text-3xl">{totals.students}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-pink-600 flex items-center gap-2"><UserCheck className="h-4 w-4" /> Girls</CardDescription>
            <CardTitle className="text-3xl text-pink-600">{totals.girls}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-blue-600 flex items-center gap-2"><UserCheck className="h-4 w-4" /> Boys</CardDescription>
            <CardTitle className="text-3xl text-blue-600">{totals.boys}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Per-day girls/boys */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-5 w-5" /> Daily Attendance (Girls / Boys)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2">
            {dates.map((d, i) => {
              const bd = byDay[d] || { female: 0, male: 0, present_total: 0, absent_total: 0 };
              return (
                <div key={d} className="rounded-lg border p-3 text-center" data-testid={`day-summary-${d}`}>
                  <p className="text-xs font-medium text-muted-foreground">{DAY_LABELS[i]}</p>
                  <p className="text-xs text-muted-foreground mb-2">{d.slice(5)}</p>
                  <div className="flex items-center justify-center gap-3 text-sm">
                    <span className="text-pink-600 font-semibold">♀ {bd.female}</span>
                    <span className="text-blue-600 font-semibold">♂ {bd.male}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="text-green-600">{bd.present_total} present</span>
                    {bd.absent_total > 0 && <> · <span className="text-red-600">{bd.absent_total} absent</span></>}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Attendance Register</CardTitle>
          <CardDescription>
            Mark Present / Absent for each student per day. Use the Save button when done.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">Loading…</div>
          ) : (grid?.rows || []).length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No students found for the selected filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[110px]">First Name</TableHead>
                    <TableHead className="min-w-[110px]">Second Name</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead className="min-w-[140px]">Parent</TableHead>
                    <TableHead className="min-w-[160px]">Address</TableHead>
                    {dates.map((d, i) => (
                      <TableHead key={d} className="text-center min-w-[100px]">
                        {DAY_LABELS[i]} <span className="text-muted-foreground font-normal text-xs">({d.slice(5)})</span>
                      </TableHead>
                    ))}
                    <TableHead className="text-center">P</TableHead>
                    <TableHead className="text-center">A</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(grid?.rows || []).map((r) => {
                    let pCount = 0;
                    let aCount = 0;
                    dates.forEach((d) => {
                      const v = getCellValue(r.student_id, d);
                      if (v === 'present' || v === 'late') pCount += 1;
                      else if (v === 'absent') aCount += 1;
                    });
                    return (
                      <TableRow key={r.student_id} data-testid={`weekly-row-${r.student_id}`}>
                        <TableCell className="font-medium">{r.first_name}</TableCell>
                        <TableCell>{r.second_name || '—'}</TableCell>
                        <TableCell>{r.student_class || '—'}</TableCell>
                        <TableCell>
                          {r.gender === 'female' && <Badge className="bg-pink-100 text-pink-800">F</Badge>}
                          {r.gender === 'male' && <Badge className="bg-blue-100 text-blue-800">M</Badge>}
                          {!r.gender && '—'}
                        </TableCell>
                        <TableCell className="text-sm">{r.parent_names || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.home_address || '—'}</TableCell>
                        {dates.map((d) => {
                          const v = getCellValue(r.student_id, d);
                          const isDirty = `${r.student_id}__${d}` in dirty;
                          return (
                            <TableCell key={d} className="text-center p-1">
                              <Select
                                value={v || 'unset'}
                                onValueChange={(val) => setCell(r.student_id, d, val === 'unset' ? '' : val)}
                                disabled={!canEdit}
                              >
                                <SelectTrigger
                                  className={cn(
                                    'h-8 w-[88px] mx-auto text-xs',
                                    v === 'present' && 'bg-green-50 border-green-300 text-green-700',
                                    v === 'absent' && 'bg-red-50 border-red-300 text-red-700',
                                    v === 'late' && 'bg-amber-50 border-amber-300 text-amber-700',
                                    isDirty && 'ring-2 ring-primary',
                                  )}
                                  data-testid={`cell-${r.student_id}-${d}`}
                                >
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unset">—</SelectItem>
                                  <SelectItem value="present">Present</SelectItem>
                                  <SelectItem value="absent">Absent</SelectItem>
                                  <SelectItem value="late">Late</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center font-semibold text-green-600">{pCount}</TableCell>
                        <TableCell className="text-center font-semibold text-red-600">{aCount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WeeklyAttendance;
